import { useState, useEffect, useCallback } from "react";
import {
  Globe, CheckCircle, Sparkles, AlertTriangle, Loader2, PlusCircle, Trash2,
  Wand2, Building2, MessageSquare, Search, Lightbulb, BookOpen, ShieldAlert,
  Zap, Info, Newspaper, ExternalLink, RefreshCw, ArrowRight, Download, RotateCcw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BACKEND_URL, STAGES, STAGE_IDS, TAGS, tagMeta, NEWS_CAT_STYLE,
  emptyStage, normaliseItem, uid, safeHostname,
} from "./constants";

// ---------- Small AI wand button ----------
function AiWand({ onClick, busy, label = "AI draft", testid, title }) {
  return (
    <Button size="sm" variant="ghost" onClick={onClick} disabled={busy}
      className="h-7 px-2 text-amber-600 hover:bg-amber-50" title={title || label} data-testid={testid}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
      <span className="ml-1 text-xs">{label}</span>
    </Button>
  );
}

// ---------- Multi-select label picker ----------
function TagPicker({ selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((t) => t !== id));
    else onChange([...selected, id]);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="text-xs justify-start min-w-[140px]" data-testid={testid || "tag-picker"}>
          {selected.length === 0 ? "Add labels…" : `${selected.length} label${selected.length > 1 ? "s" : ""}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <p className="text-xs font-medium text-slate-600 px-1 mb-1">Select one or more labels</p>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {TAGS.map((t) => (
            <label key={t.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`tag-check-${t.id}`} />
              <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} />
              <span className="text-sm text-slate-700">{t.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ==============================================================
//  Value Chain Tab
// ==============================================================
// `mode` controls which parts of this component render:
//   - "markets" : only the Market Trends & News section
//   - "canvas"  : only the value-chain canvas + stress test
//   - undefined : both (legacy)
export default function ValueChainTab({ user, companyProfile, canvasData, setCanvasData, stressTest, setStressTest, persist, competitors, onAddNewsAsCompetitor, goToCompetitorTab, mode }) {
  const [openStageId, setOpenStageId] = useState(null);
  const [runningStress, setRunningStress] = useState(false);
  const [showStress, setShowStress] = useState(false);
  const [marketNews, setMarketNews] = useState({ items: [], clusters: [], generated_at: null, loading: false, notConfigured: false });

  const showNews = mode === "markets" || mode === undefined;
  const showCanvas = mode === "canvas" || mode === undefined;

  useEffect(() => { if (user && showNews) loadMarketNews(false); }, [user, showNews]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStage = (id) => canvasData[id] || emptyStage();
  const updateStage = (id, patch) => {
    setCanvasData((prev) => ({ ...prev, [id]: { ...emptyStage(), ...(prev[id] || {}), ...patch } }));
  };

  const openStage = (stageId) => setOpenStageId(stageId);

  const runStressTest = async () => {
    setRunningStress(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/market/stress-test`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ canvasData }),
      });
      if (res.ok) { const d = await res.json(); setStressTest(d); setShowStress(true); persist({ stressTest: d }); }
      else toast.error("Failed to run stress test");
    } catch (e) { toast.error("Failed to run stress test"); }
    setRunningStress(false);
  };

  const loadMarketNews = async (refresh = false) => {
    setMarketNews((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/market/market-news`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ refresh }),
      });
      if (res.ok) {
        const d = await res.json();
        setMarketNews({
          items: d.items || [], clusters: d.clusters || [],
          generated_at: d.generated_at || null, loading: false,
          notConfigured: !!d.not_configured, error: d.error || null,
        });
        if (refresh && d.items?.length) toast.success(`Loaded ${d.items.length} updates in ${d.clusters?.length || 0} themes`);
        else if (d.error) toast.error(`Market news: ${d.error}`);
      } else setMarketNews({ items: [], clusters: [], generated_at: null, loading: false, notConfigured: false });
    } catch (e) {
      setMarketNews({ items: [], clusters: [], generated_at: null, loading: false, notConfigured: false });
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* 1) Market Trends & News (top) */}
        {showNews && (
          <MarketNewsSection
            state={marketNews}
            onRefresh={() => loadMarketNews(true)}
            competitors={competitors}
            onAddNewsAsCompetitor={onAddNewsAsCompetitor}
            goToCompetitorTab={goToCompetitorTab}
          />
        )}

        {/* 2) Value Chain canvas */}
        {showCanvas && (
        <div>
          <div className="mb-2"><Legend /></div>
          <div className="overflow-x-auto pb-3" data-testid="value-chain-canvas">
            <div className="flex gap-3 min-w-max">
              {STAGES.map((stage, idx) => (
                <CanvasColumn key={stage.id} stage={stage} index={idx} data={getStage(stage.id)} onClick={() => openStage(stage.id)} />
              ))}
            </div>
          </div>

          {/* 3) Stress-test button below canvas */}
          <div className="flex justify-end mt-3">
            <Button data-testid="stress-test-btn" variant="outline" onClick={runStressTest} disabled={runningStress} className="border-teal-300 text-teal-700 hover:bg-teal-50">
              {runningStress ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />} Stress-test our value-chain map
            </Button>
          </div>
        </div>
        )}
      </div>

      {showCanvas && (
        <>
          <StageModal
            stage={STAGES.find((s) => s.id === openStageId)} open={!!openStageId}
            data={openStageId ? getStage(openStageId) : emptyStage()}
            onClose={() => { setOpenStageId(null); persist(); }}
            onChange={(patch) => openStageId && updateStage(openStageId, patch)}
          />
          <StressTestDialog open={showStress} onClose={() => setShowStress(false)} result={stressTest} />
        </>
      )}
    </>
  );
}

// ============ Sub-components ============

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500" data-testid="legend">
      {TAGS.map((t) => (
        <span key={t.id} className="inline-flex items-center gap-1">
          <span className={`w-2.5 h-2.5 rounded-full ${t.dot}`} /> {t.label}
        </span>
      ))}
    </div>
  );
}

function CanvasColumn({ stage, index, data, onClick }) {
  const items = data.items || [];
  const answered = !!(data.create_value?.trim() || data.assumptions?.trim());
  return (
    <button onClick={onClick} data-testid={`stage-col-${stage.id}`}
      className="w-[248px] flex-shrink-0 text-left rounded-xl border bg-white hover:shadow-md transition-all flex flex-col border-slate-200">
      <div className="p-3 border-b bg-slate-50/70 rounded-t-xl">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold text-teal-600">STAGE {index + 1}</span>
          {answered && <CheckCircle className="w-3.5 h-3.5 text-teal-500" />}
        </div>
        <h3 className="text-sm font-semibold text-slate-900 leading-tight mt-1">{stage.name}</h3>
        <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{stage.what}</p>
      </div>
      <div className="p-3 flex-1 space-y-1.5 min-h-[110px]">
        {items.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Click to add players (competitors, partners, customers, enablers, blockers, gatekeepers, payers, KOLs)…</p>
        ) : (
          items.slice(0, 6).map((it) => (
            <div key={it.id} className="text-[11px] leading-snug px-2 py-1 rounded border bg-white border-slate-200">
              <div className="text-slate-800 font-medium">{it.text}</div>
              {it.note && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{it.note}</div>}
              {it.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {it.tags.map((t) => {
                    const tm = tagMeta(t);
                    return <span key={t} className={`inline-block text-[9px] px-1 py-0.5 rounded border ${tm.chip}`}>{tm.label}</span>;
                  })}
                </div>
              )}
            </div>
          ))
        )}
        {items.length > 6 && <p className="text-[11px] text-slate-400">+{items.length - 6} more</p>}
      </div>
      <div className="px-3 py-2 border-t text-[11px] text-slate-500 flex items-center gap-1">
        <span className={`w-1.5 h-1.5 rounded-full ${answered ? "bg-teal-500" : "bg-slate-300"}`} />
        {answered ? "Questions answered" : "2 open questions"}
      </div>
    </button>
  );
}

function QuestionWand({ stageId, question, currentText, stageData, onAnswer, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/market/suggest-answer`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ stageId, question, currentText, stageData }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.answer) { onAnswer(d.answer); toast.success("AI draft ready"); }
        else toast.info("No answer generated");
      } else toast.error("Failed to draft answer");
    } catch (e) { toast.error("Failed to draft answer"); }
    setBusy(false);
  };
  return <AiWand onClick={run} busy={busy} testid={testid} title="AI draft an answer (uses your profile, deck & market context)" />;
}

function StageModal({ stage, open, data, onClose, onChange }) {
  const [newText, setNewText] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newTags, setNewTags] = useState([]);
  const [suggesting, setSuggesting] = useState(false);

  if (!stage) return null;
  const items = (data.items || []);

  const addItem = () => {
    if (!newText.trim()) return;
    const item = normaliseItem({ id: uid(), text: newText.trim(), note: newNote.trim(), tags: newTags });
    onChange({ items: [...items, item] });
    setNewText(""); setNewNote(""); setNewTags([]);
  };
  const removeItem = (id) => onChange({ items: items.filter((i) => i.id !== id) });
  const updateItemTags = (id, tags) => onChange({ items: items.map((i) => i.id === id ? { ...i, tags } : i) });

  const suggestItems = async () => {
    setSuggesting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/market/suggest-stage-items`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ stageId: stage.id, existingTexts: items.map((i) => i.text) }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.items?.length) {
          const added = d.items.map((it) => normaliseItem({ id: uid(), text: it.text, note: it.note || "", tags: it.tags || (it.tag ? [it.tag] : []) })).filter(Boolean);
          onChange({ items: [...items, ...added] });
          toast.success(`Added ${added.length} AI suggestions`);
        } else if (d.message) toast.info(d.message);
        else toast.info("No suggestions returned");
      } else toast.error("Failed to get suggestions");
    } catch (e) { toast.error("Failed to get suggestions"); }
    setSuggesting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="stage-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">{stage.name}</DialogTitle>
          <DialogDescription>{stage.what}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-slate-800">Do we create value here? How?</Label>
              <QuestionWand stageId={stage.id} question="create_value" currentText={data.create_value || ""} stageData={data} onAnswer={(a) => onChange({ create_value: a })} testid="wand-create-value" />
            </div>
            <AutoResizeTextarea value={data.create_value || ""} onChange={(e) => onChange({ create_value: e.target.value })} minRows={3} maxHeight={560} className="mt-1 text-sm leading-relaxed" placeholder="Describe where and how you create value at this stage — or click AI draft." data-testid="field-create-value" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-slate-800">What are our assumptions?</Label>
              <QuestionWand stageId={stage.id} question="assumptions" currentText={data.assumptions || ""} stageData={data} onAnswer={(a) => onChange({ assumptions: a })} testid="wand-assumptions" />
            </div>
            <AutoResizeTextarea value={data.assumptions || ""} onChange={(e) => onChange({ assumptions: e.target.value })} minRows={3} maxHeight={560} className="mt-1 text-sm leading-relaxed" placeholder="List the assumptions your value at this stage depends on…" data-testid="field-assumptions" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium">Players at this stage</Label>
            <AiWand onClick={suggestItems} busy={suggesting} label="AI suggest 5" testid="stage-wand-btn" title="AI suggest up to 5 named players with a rationale" />
          </div>

          <div className="space-y-2 mb-3">
            {items.length === 0 && <p className="text-xs text-slate-400">No cards yet. Add manually or use AI suggest.</p>}
            {items.map((it) => (
              <div key={it.id} className="border rounded-lg p-2 bg-slate-50/50" data-testid={`card-item-${it.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 font-medium">{it.text}</p>
                    {it.note && <p className="text-xs text-slate-500 mt-0.5 italic">{it.note}</p>}
                  </div>
                  <button onClick={() => removeItem(it.id)} data-testid={`remove-item-${it.id}`} className="text-slate-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1 mt-2">
                  {(it.tags || []).map((t) => {
                    const tm = tagMeta(t);
                    return <span key={t} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${tm.chip}`}>{tm.label}</span>;
                  })}
                  <TagPicker selected={it.tags || []} onChange={(tags) => updateItemTags(it.id, tags)} testid={`card-tags-${it.id}`} />
                </div>
              </div>
            ))}
          </div>

          <div className="border-t pt-3">
            <p className="text-xs text-slate-500 mb-2">Add a new card</p>
            <div className="space-y-2">
              <div className="flex gap-2 items-start">
                <Input value={newText} onChange={(e) => setNewText(e.target.value)} placeholder="Name (e.g. Novartis, NICE, Prof. Jane Smith…)" className="text-sm flex-1" data-testid="new-item-input" />
                <TagPicker selected={newTags} onChange={setNewTags} testid="new-item-tag" />
                <Button size="sm" onClick={addItem} data-testid="add-item-btn"><PlusCircle className="w-4 h-4" /></Button>
              </div>
              <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()}
                placeholder="Optional rationale — why does this player matter here?" className="text-xs" data-testid="new-item-note" />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-red-600 hover:bg-red-50"
            onClick={() => {
              if (items.length === 0 && !(data.create_value || "").trim() && !(data.assumptions || "").trim()) return;
              if (window.confirm(`Reset "${stage.name}"? This will clear both answers and all ${items.length} player card(s).`)) {
                onChange({ create_value: "", assumptions: "", items: [] });
                toast.success(`${stage.name} reset`);
              }
            }}
            data-testid="stage-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset stage
          </Button>
          <Button onClick={onClose} data-testid="stage-done-btn">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function StressTestDialog({ open, onClose, result }) {
  const rows = [
    { k: "value_position_unclear",   label: "Where your value story is weakest",   icon: AlertTriangle, color: "text-amber-600" },
    { k: "biggest_competitor",       label: "Biggest competitor / future threat",  icon: ShieldAlert,   color: "text-red-600" },
    { k: "critical_gatekeeper",      label: "Most critical gatekeeper / blocker",  icon: ShieldAlert,   color: "text-orange-600" },
    { k: "under_mapped_stage",       label: "Under-mapped stage",                  icon: Info,          color: "text-slate-500" },
    { k: "assumption_to_test",       label: "Riskiest assumption to test first",   icon: AlertTriangle, color: "text-amber-600" },
    { k: "strongest_expert_question", label: "Strongest expert question",           icon: MessageSquare, color: "text-violet-600" },
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="stress-test-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-teal-600" /> Value-Chain Stress Test</DialogTitle>
          <DialogDescription>A candid critique of your map before your expert session.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {result ? rows.map((r, i) => (
            <div key={r.k} className="border rounded-lg p-3">
              <p className={`text-sm font-medium flex items-center gap-2 ${r.color}`}><span className="text-slate-400">{i + 1}.</span><r.icon className="w-4 h-4" /> {r.label}</p>
              <p className="text-sm text-slate-600 mt-1 pl-6">{result[r.k]}</p>
            </div>
          )) : <p className="text-sm text-slate-500">No stress test yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MarketNewsSection({ state, onRefresh, competitors, onAddNewsAsCompetitor, goToCompetitorTab }) {
  const { items, clusters, loading, notConfigured, generated_at, error } = state;
  const [openCluster, setOpenCluster] = useState(null);
  const displayClusters = (clusters && clusters.length > 0)
    ? clusters
    : (items || []).map((it) => ({ id: it.id, title: it.title, summary: it.summary, category: it.category, sources: [it] }));

  // Check whether a subject_company already exists in the competitor landscape (case-insensitive substring)
  const isAlreadyInLandscape = (name) => {
    if (!name) return false;
    const n = name.toLowerCase().trim();
    const MIN = 4;
    return (competitors || []).some((c) => {
      const cc = (c.company || "").toLowerCase().trim();
      if (!cc) return false;
      if (cc === n) return true;
      if (n.length >= MIN && cc.includes(n)) return true;
      if (cc.length >= MIN && n.includes(cc)) return true;
      return false;
    });
  };

  const handleAddAsCompetitor = (article) => {
    const res = onAddNewsAsCompetitor(article);
    if (res.status === "added") {
      toast.success(`Added ${article.subject_company} to Competitor Landscape`, {
        action: { label: "Open tab", onClick: () => goToCompetitorTab && goToCompetitorTab() },
      });
    } else if (res.status === "duplicate") {
      toast.warning(`${article.subject_company} is already in your Competitor Landscape as “${res.existing.company}”`);
    } else {
      toast.error("Couldn't add — no company detected in this article");
    }
  };

  return (
    <Card data-testid="market-news-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2 text-slate-800"><Newspaper className="w-4 h-4 text-teal-600" /> Market trends & news</CardTitle>
          <div className="flex items-center gap-2">
            {generated_at && <span className="text-[10px] text-slate-400">Updated {new Date(generated_at).toLocaleDateString()}</span>}
            <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading} className="h-7 px-2 text-teal-600 hover:bg-teal-50" data-testid="market-news-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span className="ml-1 text-xs">{displayClusters.length === 0 ? "Fetch" : "Refresh"}</span>
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500">Higher-level themes from the last 12 months of pharma news — framed by impact on your company. Click a card to see the underlying articles.</p>
      </CardHeader>
      <CardContent>
        {notConfigured ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Market news is powered by the Tavily web-search API. Ask your admin to set <code className="bg-amber-100 px-1 rounded">TAVILY_API_KEY</code> in the backend to activate this section.
          </div>
        ) : loading && displayClusters.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Searching the last 12 months of pharma news…</div>
        ) : displayClusters.length === 0 ? (
          <div className="text-xs text-slate-500 py-4">{error ? `Couldn't fetch news: ${error}. ` : ""}Click <em>Fetch</em> to pull recent market updates relevant to your value chain.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {displayClusters.map((c) => {
              const cat = NEWS_CAT_STYLE[c.category] || NEWS_CAT_STYLE.other;
              const nSources = c.sources?.length || 0;
              return (
                <button type="button" key={c.id} data-testid={`market-cluster-card-${c.id}`} onClick={() => setOpenCluster(c)}
                  className="group text-left block border rounded-lg p-3 bg-white hover:shadow-md hover:border-teal-300 transition-all">
                  <div className="flex items-center gap-2 mb-1.5">
                    {c.category && <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border capitalize ${cat}`}>{c.category}</span>}
                    <span className="ml-auto text-[10px] text-slate-400">{nSources > 1 ? `${nSources} articles` : "1 article"}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-800 leading-snug group-hover:text-teal-700">{c.title}</p>
                  {c.summary && <p className="text-xs text-slate-500 mt-1.5 leading-relaxed line-clamp-4">{c.summary}</p>}
                  <div className="flex items-center gap-1 mt-2 text-[10px] text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink className="w-3 h-3" /> View {nSources > 1 ? "articles" : "article"} →
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!openCluster} onOpenChange={(o) => { if (!o) setOpenCluster(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="market-cluster-dialog">
          {openCluster && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border capitalize ${NEWS_CAT_STYLE[openCluster.category] || NEWS_CAT_STYLE.other}`}>{openCluster.category}</span>
                  <span className="text-[10px] text-slate-400">{openCluster.sources.length} article{openCluster.sources.length > 1 ? "s" : ""}</span>
                </div>
                <DialogTitle className="text-base leading-snug">{openCluster.title}</DialogTitle>
                {openCluster.summary && <DialogDescription className="pt-1 text-sm text-slate-600 leading-relaxed">{openCluster.summary}</DialogDescription>}
              </DialogHeader>
              <div className="space-y-2 mt-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Underlying articles</p>
                {openCluster.sources.map((s) => {
                  const isCompetitor = s.category === "competitor" && !!s.subject_company;
                  const alreadyIn = isCompetitor && isAlreadyInLandscape(s.subject_company);
                  return (
                    <div key={s.id} data-testid={`cluster-source-${s.id}`}
                      className="border rounded-lg p-3 bg-white hover:bg-slate-50 hover:border-teal-300 transition-all">
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="group block">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] text-slate-400">{s.published || safeHostname(s.url)}</span>
                          <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-teal-600 ml-auto" />
                        </div>
                        <p className="text-sm font-medium text-slate-800 group-hover:text-teal-700 leading-snug">{s.title}</p>
                        {s.summary && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.summary}</p>}
                        <p className="text-[10px] text-slate-400 mt-1">{s.source || safeHostname(s.url)}</p>
                      </a>
                      {isCompetitor && (
                        <div className="mt-2 pt-2 border-t border-dashed border-slate-200 flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <span className="text-[11px] text-slate-500 flex-1 truncate">
                            Detected competitor: <span className="font-medium text-slate-700">{s.subject_company}</span>
                          </span>
                          {alreadyIn ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200" data-testid={`news-comp-already-${s.id}`}>Already in landscape</span>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                              onClick={(e) => { e.stopPropagation(); handleAddAsCompetitor(s); }}
                              data-testid={`news-add-competitor-${s.id}`}
                            >
                              <PlusCircle className="w-3.5 h-3.5 mr-1" /> Add as competitor
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
